import { motion } from "framer-motion";
import { Camera, Globe2, MapPin, ArrowRight, Sparkles, Home, Users, PlaneTakeoff, CheckCircle2 } from "lucide-react";
import Layout from "@/components/site/Layout";
import PageHero from "@/components/site/PageHero";
import { CLIENT_EXPECTATIONS, OC_STONE_HOME_URL } from "@/lib/assets";

const PLATFORMS = ["Zillow", "Trulia", "Homes.com", "Realtor.com", "Redfin", "MLS.com", "Realty ONE Group", "ListHub"];

const CHANNELS = [
  {
    icon: Home,
    tag: "Foundation",
    title: "CRMLS · Where Your Listing Begins",
    desc: "Your home is entered into the California Regional Multiple Listing Service — the largest MLS in the nation — giving real estate professionals throughout California immediate access to accurate, up-to-date listing information. This is the foundation every other marketing channel builds on.",
    stat: "#1",
    statLabel: "Largest MLS in the U.S.",
    image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80",
  },
  {
    icon: Globe2,
    tag: "Digital Syndication",
    title: "Leading Online Marketplaces",
    desc: "From CRMLS, your listing automatically syndicates to the platforms today's buyers actually use to search — including Zillow, Trulia, Homes.com, Realtor.com, Redfin, and other major real estate marketplaces — so your home shows up wherever buyers are looking.",
    stat: "6+",
    statLabel: "Major U.S. portals",
    image: "https://images.unsplash.com/photo-1512428559087-560fa5ceab42?w=1200&q=80",
  },
  {
    icon: PlaneTakeoff,
    tag: "International Reach",
    title: "100+ International Websites, 60+ Countries",
    desc: "We go beyond the U.S. market. Through our affiliation with Realty ONE Group and a partnership with ListHub, your listing reaches more than 100 trusted international websites across 60+ countries, in their native languages — including top real estate platforms in China and other key luxury markets.",
    stat: "60+",
    statLabel: "Countries · native languages",
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&q=80",
  },
  {
    icon: Users,
    tag: "In-Person Marketing",
    title: "Personal, Face-to-Face Marketing",
    desc: "Digital reach matters, but we haven't abandoned what's always worked. We host open houses to showcase your home in person, and distribute flyers to neighbors and prospective buyers in the surrounding community. Selling an inherited home doesn't have to feel impersonal — we make sure real conversations and real relationships are still part of the process.",
    stat: "1:1",
    statLabel: "Real conversations",
    image: "https://images.pexels.com/photos/7578891/pexels-photo-7578891.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    icon: Camera,
    tag: "Cinematic Media",
    title: "Aerial Photography & Video",
    desc: "For standout and luxury listings, we bring in professional drone photography and video to capture your property and its surroundings in ways ground-level photos simply can't. Paired with professional photography, this gives buyers a true sense of the home before they ever walk through the door.",
    stat: "4K",
    statLabel: "Drone + professional stills",
    image: "https://images.unsplash.com/photo-1473968512647-3e447244af8f?w=1200&q=80",
  },
];

export default function MarketYourHome() {
  return (
    <Layout>
      <PageHero
        breadcrumb="Home / Market Your Home"
        overline="How We Market Your Probate or Inherited Home"
        title="More than a yard sign —"
        accentWord="a strategy built for estates."
        description={
          <>
            <p>
              Selling a home you&apos;ve inherited isn&apos;t the same as a typical listing. Buyers need to understand the property may be sold as-is, the timeline may involve court requirements, and the seller — often an executor or administrator acting on behalf of an estate — needs a strategy that protects the estate&apos;s value while minimizing stress during an already difficult time.
            </p>
            <p>
              That&apos;s where we come in. We combine the reach of modern digital marketing with the personal, face-to-face approach that has always sold homes — open houses, neighborhood outreach, and direct buyer relationships — so your inherited property gets seen by the right buyers and sells for the right price.
            </p>
          </>
        }
        image="https://images.unsplash.com/photo-1602110604609-b40d3dff23c2?w=1600&q=80"
        rightImage={OC_STONE_HOME_URL}
        rightImageAlt="Beautifully landscaped Orange County home"
      />

      {/* When you work with us — expectations */}
      <section className="py-20 bg-white" data-testid="expectations-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.24em] text-gold-700 font-semibold">When you work with us, you can expect to:</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl text-navy-900 leading-tight">
              Every detail — <em className="italic text-gold-600">handled</em>.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-3" data-testid="expectations-list">
            {CLIENT_EXPECTATIONS.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                data-testid={`marketing-expectation-${i}`}
                className={`flex items-start gap-3 rounded-xl border p-4 transition-all ${
                  item.startsWith("Give love")
                    ? "border-gold-500/40 bg-gold-500/5"
                    : "border-navy-100 bg-navy-50/40 hover:border-gold-400 hover:bg-white hover:shadow-sm"
                }`}
              >
                <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-gold-600" />
                <span className={`text-sm text-navy-700 ${item.startsWith("Give love") ? "italic font-semibold text-gold-700" : ""}`}>{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Marketing channels — sequenced */}
      <section className="py-20 bg-navy-50/40" data-testid="channels-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="section-overline">Our Marketing Channels</span>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl text-navy-900 leading-tight">
              Five layers of <em className="italic text-gold-600">reach</em>. One goal: sell for the right price.
            </h2>
          </div>

          <div className="space-y-6">
            {CHANNELS.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`channel-${i}`}
                className="rounded-3xl border border-navy-100 bg-white overflow-hidden hover:border-gold-400 hover:shadow-xl transition-all grid lg:grid-cols-12"
              >
                <div className="lg:col-span-4 relative min-h-[220px]">
                  <img src={c.image} alt={c.title} className="absolute inset-0 h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-white/30 lg:to-white" />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-navy-800">
                    <c.icon size={12} className="text-gold-600" />
                    {c.tag}
                  </div>
                  <div className="absolute bottom-4 left-4 font-heading text-5xl text-white drop-shadow-lg lg:text-gold-500 lg:drop-shadow-none">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </div>
                <div className="lg:col-span-5 p-7 sm:p-9">
                  <h3 className="font-heading text-2xl text-navy-900 leading-tight">{c.title}</h3>
                  <p className="mt-3 text-sm text-navy-600 leading-relaxed">{c.desc}</p>
                </div>
                <div className="lg:col-span-3 p-7 sm:p-9 lg:border-l border-navy-100 flex flex-col justify-center">
                  <div className="font-heading text-5xl text-gold-600 leading-none">{c.stat}</div>
                  <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-navy-500 font-semibold">{c.statLabel}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform marquee */}
      <section className="py-14 bg-white border-y border-navy-100" data-testid="platform-marquee">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center text-xs uppercase tracking-[0.24em] text-navy-500 font-semibold mb-6">
            Your listing syndicated across
          </div>
          <div className="relative overflow-hidden">
            <div className="flex animate-marquee gap-14 w-max items-center">
              {[...PLATFORMS, ...PLATFORMS].map((p, i) => (
                <div key={i} className="text-navy-500 font-heading text-2xl italic whitespace-nowrap tracking-wide hover:text-gold-600 transition-colors">
                  {p}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-navy-900 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-gold-500/15 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-gold-600/10 blur-3xl" />
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 relative text-center">
          <Sparkles size={40} className="mx-auto text-gold-400" />
          <h2 className="mt-6 font-heading text-4xl sm:text-5xl text-white leading-tight">
            Let's get your home in front of <em className="italic text-gold-400">the right buyers</em>.
          </h2>
          <p className="mt-5 text-white/70 max-w-2xl mx-auto">
            Marketing a probate or inherited property well takes more than a yard sign — it takes a strategy built specifically for estate sales, backed by real reach and real relationships.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a href="tel:+17143913645" data-testid="market-cta-phone" className="btn-gold group">
              Call (714) 391-3645
              <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
            </a>
            <a href="#free-package-callout" data-testid="market-cta-package" className="inline-flex items-center justify-center rounded-full border border-white/20 text-white px-7 py-3.5 text-sm font-medium hover:bg-white hover:text-navy-900 transition-all">
              Request Your Free Package
            </a>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-xs text-white/60">
            <MapPin size={12} className="text-gold-400" />
            2134 Main St. Suite 140, Huntington Beach, CA 92648
          </div>
        </div>
      </section>
    </Layout>
  );
}
